import React from "react";
import "./FAQ.css";

function FAQ() {
  return (
    <div className="faqpage-container">
      <header className="faqpage-header">
        <div className="faqpage-header-content">
          <h1>Frequently Asked Questions</h1>
          <p className="faqpage-last-updated">Last updated: November 27, 2025</p>
        </div>
      </header>

      <div className="faqpage-content">
        {/* Sidebar Navigation */}
        <aside className="faqpage-nav">
          <h3>Contents</h3>
          <ul>
            <li><a href="#general">General</a></li>
            <li><a href="#features">Features & Capabilities</a></li>
            <li><a href="#audio-video">Audio & Video</a></li>
            <li><a href="#exports">Exports & Editing</a></li>
            <li><a href="#usage">Usage & Limits</a></li>
            <li><a href="#pricing">Pricing & Plans</a></li>
            <li><a href="#support">Support</a></li>
          </ul>
        </aside>

        {/* Main FAQ Sections */}
        <main className="faqpage-main">
          {/* General */}
          <section id="general" className="faqpage-section">
            <h2>General</h2>
            <p><strong>What is Cayoty?</strong><br/>
              Cayoty is an AI-powered platform that transforms text prompts or audio recordings into professional presentations. Generate slides from descriptions or create synchronized video presentations from audio files.
            </p>
            <p><strong>Do I need design skills?</strong><br/>
              No! Cayoty automatically handles layouts, formatting, visual hierarchy, and adds professional stock imagery. Just provide your content and get polished slides.
            </p>
            <p><strong>Who is Cayoty for?</strong><br/>
              Cayoty is designed for online educators, course creators, marketing teams, content creators, sales teams, consultants, training managers, YouTubers, teachers, coaches, business owners, podcasters, agencies, nonprofits, researchers, startups, HR teams, product managers, freelancers, event speakers, and influencers.
            </p>
          </section>

          {/* Features */}
          <section id="features" className="faqpage-section">
            <h2>Features & Capabilities</h2>
            <p><strong>How do the two modes work?</strong><br/>
              Prompt to Slide: Describe your presentation in text (30-60 seconds). Cayoty's AI analyzes your input, structures the content, and generates professionally designed slides with layouts, formatting, and visual hierarchy.<br/><br/>
              Audio to Slide: Upload lecture recordings, meeting recordings, or podcasts. Cayoty generates corresponding slides based on the audio content and produces a 1080p video with your audio perfectly synchronized to slide transitions.
            </p>
            <p><strong>What makes Cayoty different from other tools?</strong><br/>
              Cayoty eliminates the need to juggle multiple tools. Instead of building slides, recording audio, then syncing everything in a video editor, you simply upload your audio and receive a professionally produced 1080p video with intelligently timed transitions that flow naturally with your narration.
            </p>
            <p><strong>Can Cayoty save me time on presentation creation?</strong><br/>
              Absolutely! Design shouldn't be a bottleneck to communication. Provide a text prompt, and Cayoty generates multiple professionally designed options instantly. No template hunting, no layout adjustments, no starting from a blank canvas—just curated choices that save you hours of work.
            </p>
          </section>

          {/* Audio & Video */}
          <section id="audio-video" className="faqpage-section">
            <h2>Audio & Video</h2>
            <p><strong>What audio formats are supported?</strong><br/>
              MP3, WAV, and M4A files up to 100MB. Audio length limits vary by plan: Free/Starter (10 min), Creator (40 min), Professional (60 min).
            </p>
            <p><strong>What types of audio can I upload?</strong><br/>
              You can upload lecture recordings, meeting recordings, podcasts, or any audio content. Cayoty will generate corresponding slides and sync them to your audio for a professional 1080p video output.
            </p>
            <p><strong>HD Video Generation taking too long?</strong><br/>
              Video sync generation takes time according to your video length. If it's a 5-minute audio, it will take between 5 to 10 minutes to sync and generate HD video. Check your internet connection for optimal performance.
            </p>
            <p><strong>What resolution are the exported videos?</strong><br/>
              All video exports are in 1080p HD quality with automatic transitions and timing that sync perfectly with your audio content.
            </p>
          </section>

          {/* Exports & Editing */}
          <section id="exports" className="faqpage-section">
            <h2>Exports & Editing</h2>
            <p><strong>What export formats are available?</strong><br/>
              Export as PowerPoint (PPTX), PNG images, or HD video. PPTX and PNG exports are unlimited on all plans. Video exports include automatic transitions and timing or duration selection.
            </p>
            <p><strong>Can I use one creation across multiple platforms?</strong><br/>
              Yes! Export a single creation as an editable PowerPoint for collaboration, broadcast-quality video for learning and presentation platforms, or optimized images for social distribution. Create once, publish everywhere—without friction, rework, or duplicated effort.
            </p>
            <p><strong>Can I edit the generated slides?</strong><br/>
              You can upload images and select the designs directly in the platform before exporting. Export as PPTX for full editing of texts in PowerPoint.
            </p>
            <p><strong>Does Cayoty include professional imagery?</strong><br/>
              Yes! All plans include professional stock imagery that's automatically added to enhance your slides.
            </p>
          </section>

          {/* Usage */}
          <section id="usage" className="faqpage-section">
            <h2>Usage & Limits</h2>
            <p><strong>How many presentations do I get?</strong><br/>
              Free: 5 projects/month<br/>
              Starter: 70 projects/month<br/>
              Creator: 200 projects/month<br/>
              Professional: 350 projects/month<br/><br/>
              Check your Sidebar for current balance and usage.
            </p>
            <p><strong>How do I write effective prompts?</strong><br/>
              Be specific! Example: "Create a 10-slide pitch deck about renewable energy for investors" works better than "slides about solar". The more detail you provide, the better Cayoty can structure and design your presentation.
            </p>
            <p><strong>Can I paste existing content?</strong><br/>
              Absolutely! Paste articles, notes, or outlines into the prompt. Cayoty will analyze and structure your content into a formatted presentation with proper slide breaks.
            </p>
            <p><strong>Are my presentations saved?</strong><br/>
              Yes! All generations are automatically saved in your dashboard history. Access "My Presentations" anytime to view and download previous creations.
            </p>
            <p><strong>Do I get priority processing?</strong><br/>
              Starter, Creator, and Professional plans all include priority processing queue for faster generation times.
            </p>
          </section>

          {/* Pricing */}
          <section id="pricing" className="faqpage-section">
            <h2>Pricing & Plans</h2>
            <p><strong>What plans are available?</strong><br/>
              <strong>Free ($0):</strong> 5 projects/month, text-to-slides generation, audio transcription (10 min max), 5 video exports (1080p), professional stock imagery, community support<br/><br/>
              <strong>Starter ($12.99/month):</strong> 70 projects/month, text-to-slides generation, audio transcription (10 min max), 20 video exports (1080p), priority processing queue, email support<br/><br/>
              <strong>Creator ($29.99/month):</strong> 200 projects/month, audio transcription (40 min max), 80 video exports (1080p), priority processing queue, advanced template library, priority support<br/><br/>
              <strong>Professional ($59.99/month):</strong> 350 projects/month, audio transcription (60 min max), 150 video exports (1080p), priority processing queue, advanced template library, dedicated support
            </p>
            <p><strong>Can I try Cayoty for free?</strong><br/>
              Yes! Start with the Free plan to try AI-powered presentation creation with 5 projects per month and no credit card required.
            </p>
            <p><strong>What's included in video exports?</strong><br/>
              Video exports vary by plan and include 1080p HD quality with automatic transitions synced to your audio. Free: 5 exports, Starter: 20 exports, Creator: 80 exports, Professional: 150 exports per month.
            </p>
            <p><strong>Are PowerPoint and image exports limited?</strong><br/>
              No! PPTX and PNG exports are unlimited on all plans, including the free plan.
            </p>
          </section>

          {/* Support */}
          <section id="support" className="faqpage-section">
            <h2>Support</h2>
            <p><strong>Need help or have questions?</strong><br/>
              Contact our support team at Support@cayoty.com, We typically respond within 1 hour during business hours.
            </p>
            <p><strong>What type of support do I get with each plan?</strong><br/>
              Free: Community support<br/>
              Starter: Email support<br/>
              Creator: Priority support<br/>
              Professional: Dedicated support
            </p>
          </section>
        </main>
      </div>
    </div>
  );
}

export default FAQ;